'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import posthog from 'posthog-js';
import { tw } from '@tw';
import type { GeneratedQuizDto } from '@peach/types/quizzes/generated-quiz.dto.js';
import { composeQuiz, postComposedQuiz } from '@/app/actions/quizzes';
import { callAction } from '@/lib/call-action';
import { toCreateBody } from '@peach/types/quizzes/create-body.js';
import { QuizPostEditor } from './QuizPostEditor';
import s from './sub.module.css';

interface QuizComposerProps {
  username: string;
  firstName: string;
  picture: string | null;
}

/**
 * The owner's composer at the top of their feed, the way a post is started on Facebook:
 * one line, the quiz drafted from it in their voice, read and edited, then posted.
 */
export function QuizComposer({ username, firstName, picture }: QuizComposerProps) {
  const router = useRouter();
  const [topic, setTopic] = useState('');
  const [draft, setDraft] = useState<GeneratedQuizDto | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const track = (event: string, props?: Record<string, unknown>) =>
    posthog.capture(event, {
      creator_username: username,
      provider_username: username,
      tutor_username: username,
      ...props,
    });

  const write = async () => {
    if (topic.trim().length < 3) return setError('Say what the quiz is about in a few words');
    setBusy(true);
    setError(null);
    track('quiz_compose_started');
    const result = await callAction(() => composeQuiz(topic.trim()));
    setBusy(false);
    if (!result.success) return setError('The quiz was not written this time. Try again.');
    setDraft(result.data);
  };

  const post = async (edited: GeneratedQuizDto) => {
    const result = await callAction(() => postComposedQuiz(toCreateBody(edited), username));
    if (!result.success) return result.error;
    track('quiz_compose_posted', { question_count: edited.questions.length });
    setDraft(null);
    setTopic('');
    router.refresh();
    return null;
  };

  if (draft) {
    return (
      <QuizPostEditor
        quiz={draft}
        source={draft}
        who={{ firstName, picture }}
        byline={`New quiz, ${draft.questions.length} questions. Only you can see it until you post it`}
        postLabel="Post quiz"
        cancelLabel="Start over"
        onSave={post}
        onCancel={() => setDraft(null)}
      />
    );
  }

  return (
    <article className={s.post}>
      <header className={s.postHead}>
        {picture ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={picture} alt="" className={s.postTile} />
        ) : (
          <span className={s.postTile} aria-hidden="true">
            {firstName.charAt(0)}
          </span>
        )}
        <p className={s.postWho}>
          Post a quiz
          <span>Your clients play it here, free</span>
        </p>
      </header>

      {!busy && (
        <>
          <label htmlFor="compose-topic" className="sr-only">
            What is your quiz about?
          </label>
          <textarea
            id="compose-topic"
            rows={2}
            maxLength={200}
            className={s.compose}
            placeholder="What is your quiz about? Phrasal verbs for small talk, say"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            onKeyDown={(e) => {
              // Shift+Enter keeps a line break; an IME confirming a word must not submit
              if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
              e.preventDefault();
              void write();
            }}
          />
          <div className={s.composeRow}>
            <p className={s.postNote}>
              10 questions in your voice. You read them before they go live.
            </p>
            <button type="button" className={tw(s.cta, s.press)} onClick={() => void write()}>
              Write it
            </button>
          </div>
        </>
      )}

      {busy && (
        <div className={s.band} role="status" aria-live="polite">
          <div className={s.writing}>
            <span className={s.spin} aria-hidden="true" />
            <p className={s.bandQ}>Writing your quiz</p>
          </div>
          <p className={s.bandCount} style={{ marginTop: 8 }}>
            10 questions about {topic.trim()}, in your voice. Usually under a minute.
          </p>
        </div>
      )}

      {error && <p className={s.postError}>{error}</p>}
    </article>
  );
}
